import { useCallback, useEffect, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import {
  ALL_SEVERITIES,
  PAGE_SIZES,
  type Severity,
} from '../SystemLog/systemLog.types';
import {
  AGENT_OPTIONS,
  type AgentHistoryFilters,
  type DateRangePreset,
} from './agentHistory.types';

// 권위: docs/ARCH_DASHBOARD.md §4.4 필터 사양.
// - Severity 칩 (화면 8 과 동일 스타일).
// - Agent multi-select 드롭다운 (LLM_F 까지 확장).
// - 기간 preset 라디오 (오늘 / 1주 / 1개월 / 3개월 / Custom). Custom 일 때만 from/to 입력.
// - Event code glob + 검색 인풋.
// - 미확인만 토글.
// - trace_id / run_id 입력 (보통 딥링크로 채워지며 수동 수정도 가능).

interface Props {
  filters: AgentHistoryFilters;
  onChange: (next: AgentHistoryFilters) => void;
  onReset: () => void;
}

const PRESET_LABELS: Record<DateRangePreset, string> = {
  today: '오늘',
  '1w': '1주',
  '1m': '1개월',
  '3m': '3개월',
  custom: '직접',
};

export function AgentHistoryFilterBar({ filters, onChange, onReset }: Props): JSX.Element {
  const [qLocal, setQLocal] = useState(filters.q);
  const [codeLocal, setCodeLocal] = useState(filters.eventCode);
  const [traceLocal, setTraceLocal] = useState(filters.traceId);
  const [runLocal, setRunLocal] = useState(filters.runId);
  const timerRef = useRef<number | null>(null);

  useEffect(() => setQLocal(filters.q), [filters.q]);
  useEffect(() => setCodeLocal(filters.eventCode), [filters.eventCode]);
  useEffect(() => setTraceLocal(filters.traceId), [filters.traceId]);
  useEffect(() => setRunLocal(filters.runId), [filters.runId]);

  const scheduleCommit = useCallback(
    (next: AgentHistoryFilters) => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        onChange(next);
      }, 400);
    },
    [onChange]
  );
  useEffect(
    () => () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    },
    []
  );

  const toggleSeverity = useCallback(
    (sev: Severity) => {
      const has = filters.severities.includes(sev);
      const next = has
        ? filters.severities.filter((s) => s !== sev)
        : [...filters.severities, sev];
      onChange({ ...filters, severities: next, page: 0 });
    },
    [filters, onChange]
  );

  const toggleAgent = useCallback(
    (a: string) => {
      const has = filters.agents.includes(a);
      const next = has ? filters.agents.filter((s) => s !== a) : [...filters.agents, a];
      onChange({ ...filters, agents: next, page: 0 });
    },
    [filters, onChange]
  );

  const [agentOpen, setAgentOpen] = useState(false);
  const agentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!agentOpen) return;
    function onDoc(e: MouseEvent): void {
      if (!agentRef.current) return;
      if (!agentRef.current.contains(e.target as Node)) setAgentOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [agentOpen]);

  return (
    <div className="syslog-filter" data-testid="agent-history-filter">
      <div className="syslog-filter__group">
        <span className="syslog-filter__label">Severity</span>
        <div className="syslog-chip-row">
          {ALL_SEVERITIES.map((sev) => {
            const active = filters.severities.includes(sev);
            return (
              <button
                key={sev}
                type="button"
                className={`syslog-chip syslog-chip--${sev.toLowerCase()}${
                  active ? ' syslog-chip--active' : ''
                }`}
                onClick={() => toggleSeverity(sev)}
              >
                {sev}
              </button>
            );
          })}
        </div>
      </div>

      <div className="syslog-filter__group" ref={agentRef}>
        <span className="syslog-filter__label">Agent</span>
        <button
          type="button"
          className="syslog-input"
          onClick={() => setAgentOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={agentOpen}
        >
          {filters.agents.length === 0 ? '전체' : filters.agents.join(', ')}
        </button>
        {agentOpen && (
          <div className="syslog-filter__dropdown" role="listbox">
            {AGENT_OPTIONS.map((a) => {
              const active = filters.agents.includes(a);
              return (
                <label key={a} className="syslog-filter__dropdown-row">
                  <input type="checkbox" checked={active} onChange={() => toggleAgent(a)} />
                  <span>{a}</span>
                </label>
              );
            })}
            {filters.agents.length > 0 && (
              <button
                type="button"
                className="syslog-btn syslog-btn--ghost"
                onClick={() => onChange({ ...filters, agents: [], page: 0 })}
              >
                선택 해제
              </button>
            )}
          </div>
        )}
      </div>

      <div className="syslog-filter__group">
        <span className="syslog-filter__label">기간</span>
        <div className="syslog-chip-row" role="radiogroup" aria-label="기간 preset">
          {(Object.keys(PRESET_LABELS) as DateRangePreset[]).map((p) => {
            const active = filters.preset === p;
            return (
              <button
                key={p}
                type="button"
                role="radio"
                aria-checked={active}
                className={`syslog-chip syslog-chip--info${active ? ' syslog-chip--active' : ''}`}
                onClick={() => onChange({ ...filters, preset: p, page: 0 })}
              >
                {PRESET_LABELS[p]}
              </button>
            );
          })}
        </div>
      </div>

      {filters.preset === 'custom' && (
        <>
          <div className="syslog-filter__group">
            <span className="syslog-filter__label">From</span>
            <input
              className="syslog-input"
              type="datetime-local"
              value={filters.from}
              onChange={(e) => onChange({ ...filters, from: e.target.value, page: 0 })}
            />
          </div>
          <div className="syslog-filter__group">
            <span className="syslog-filter__label">To</span>
            <input
              className="syslog-input"
              type="datetime-local"
              value={filters.to}
              onChange={(e) => onChange({ ...filters, to: e.target.value, page: 0 })}
            />
          </div>
        </>
      )}

      <div className="syslog-filter__group">
        <span className="syslog-filter__label">Event code</span>
        <input
          className="syslog-input"
          type="text"
          placeholder="예: LEADING.PIPELINE.*"
          value={codeLocal}
          onChange={(e) => {
            setCodeLocal(e.target.value);
            scheduleCommit({ ...filters, eventCode: e.target.value, page: 0 });
          }}
        />
      </div>

      <div className="syslog-filter__group syslog-filter__group--grow">
        <span className="syslog-filter__label">검색 (summary)</span>
        <input
          className="syslog-input"
          type="text"
          placeholder="키워드 입력"
          value={qLocal}
          onChange={(e) => {
            setQLocal(e.target.value);
            scheduleCommit({ ...filters, q: e.target.value, page: 0 });
          }}
        />
      </div>

      <div className="syslog-filter__group">
        <span className="syslog-filter__label">trace_id</span>
        <input
          className="syslog-input"
          type="text"
          placeholder="UUID"
          value={traceLocal}
          onChange={(e) => {
            setTraceLocal(e.target.value);
            scheduleCommit({ ...filters, traceId: e.target.value, page: 0 });
          }}
        />
      </div>

      <div className="syslog-filter__group">
        <span className="syslog-filter__label">run_id</span>
        <input
          className="syslog-input"
          type="text"
          placeholder="예: leading-20260420"
          value={runLocal}
          onChange={(e) => {
            setRunLocal(e.target.value);
            scheduleCommit({ ...filters, runId: e.target.value, page: 0 });
          }}
        />
      </div>

      <div className="syslog-filter__group">
        <span className="syslog-filter__label">&nbsp;</span>
        <label className="syslog-filter__inline">
          <input
            type="checkbox"
            checked={filters.unackedOnly}
            onChange={(e) => onChange({ ...filters, unackedOnly: e.target.checked, page: 0 })}
          />
          <span>미확인만</span>
        </label>
      </div>

      <div className="syslog-filter__group">
        <span className="syslog-filter__label">Page size</span>
        <select
          className="syslog-input"
          value={filters.pageSize}
          onChange={(e) =>
            onChange({ ...filters, pageSize: parseInt(e.target.value, 10), page: 0 })
          }
        >
          {PAGE_SIZES.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>

      <div className="syslog-filter__group">
        <span className="syslog-filter__label">&nbsp;</span>
        <button type="button" className="syslog-btn syslog-btn--ghost" onClick={onReset}>
          <RotateCcw size={14} strokeWidth={1.75} />
          초기화
        </button>
      </div>
    </div>
  );
}
